'use client';

import { useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { useParams, usePathname, useSearchParams } from 'next/navigation';
import styles from './page.module.scss';
import { Calendar, Clock, ExternalLink, Lock, Plane, Radar, UserCheck } from 'lucide-react';
import { SectorContexts } from '@/src/components/hestiaDesign/SectorContexts/SectorContexts';
import {
  CockpitResult,
  ErrorState,
  FullPlanEditor,
  GraphLoader,
  LeadForm,
  PromptForm,
  ReasoningChat,
} from '@/src/components/northStarMeasurementPlan';
import { trackMeasurementPlanEvent } from '@/src/lib/measurementPlan/analytics';
import { buildConfluenceMarkdown } from '@/src/lib/measurementPlan/confluenceTemplate';
import type { MeasurementPlan } from '@/src/lib/measurementPlan/schema';

const SECTORS = [
  { id: '', label: { fr: 'Secteur (optionnel)', en: 'Sector (optional)' } },
  { id: 'pharma', label: { fr: 'Pharma / Life Sciences', en: 'Pharma / Life Sciences' } },
  { id: 'supply-chain', label: { fr: 'Supply Chain / Logistics', en: 'Supply Chain / Logistics' } },
  { id: 'industrial', label: { fr: 'Industrial / IoT', en: 'Industrial / IoT' } },
  { id: 'legal', label: { fr: 'Legal / Compliance', en: 'Legal / Compliance' } },
  { id: 'finance', label: { fr: 'Finance / Wealth', en: 'Finance / Wealth' } },
  { id: 'operations', label: { fr: 'Corporate', en: 'Corporate' } },
  { id: 'education', label: { fr: 'Éducation / EdTech', en: 'Education / EdTech' } },
];

const MVP_SUGGESTIONS = [
  {
    sector: 'pharma',
    tag: { fr: 'Pharma / Life Sciences', en: 'Pharma / Life Sciences' },
    title: { fr: 'Revue metadata clinique', en: 'Clinical metadata review' },
    fullText: {
      fr: 'Accélérer la revue des datasets, metadata et documentation clinique avec un cockpit IA métier mesurable.',
      en: 'Speed up dataset, metadata and clinical documentation review with a measurable business AI cockpit.',
    },
    prompt: {
      fr: 'La revue des metadata cliniques prend trop de temps',
      en: 'Clinical metadata review takes too long',
    },
  },
  {
    sector: 'supply-chain',
    tag: { fr: 'Supply Chain / Logistics', en: 'Supply Chain / Logistics' },
    title: { fr: 'Choix transporteur optimal', en: 'Carrier lane selection' },
    fullText: {
      fr: 'Optimiser le choix transporteur par lane en réduisant le temps de décision et les coûts logistiques.',
      en: 'Optimize carrier selection per lane by reducing decision time and logistics costs.',
    },
    prompt: {
      fr: 'Le choix transporteur par lane est trop lent et coûteux',
      en: 'Carrier selection per lane is too slow and costly',
    },
  },
  {
    sector: 'industrial',
    tag: { fr: 'Industrial / IoT', en: 'Industrial / IoT' },
    title: { fr: 'Qualité performance industrielle', en: 'Industrial quality metrics' },
    fullText: {
      fr: 'Piloter la qualité et la performance des lignes industrielles avec des métriques temps réel et un MVP ciblé.',
      en: 'Steer industrial quality and line performance with real-time metrics and a targeted MVP.',
    },
    prompt: {
      fr: 'La qualité de production est difficile à piloter en temps réel',
      en: 'Production quality is hard to steer in real time',
    },
  },
  {
    sector: 'legal',
    tag: { fr: 'Legal / Compliance', en: 'Legal / Compliance' },
    title: { fr: 'Veille réglementaire ciblée', en: 'Regulatory watch compliance' },
    fullText: {
      fr: 'Faciliter la veille réglementaire et la traçabilité des documents juridiques avec un assistant métier.',
      en: 'Make regulatory watch and legal document traceability easier with a business assistant.',
    },
    prompt: {
      fr: 'La veille réglementaire et la compliance sont trop chronophages',
      en: 'Regulatory watch and compliance take too much time',
    },
  },
  {
    sector: 'finance',
    tag: { fr: 'Finance / Wealth', en: 'Finance / Wealth' },
    title: { fr: 'Reporting financier décisionnel', en: 'Financial reporting speed' },
    fullText: {
      fr: 'Fiabiliser et accélérer le reporting financier pour des décisions de gestion plus rapides et plus sûres.',
      en: 'Make financial reporting more reliable and faster for quicker, safer management decisions.',
    },
    prompt: {
      fr: 'Le reporting financier est trop manuel et source d’erreurs',
      en: 'Financial reporting is too manual and error-prone',
    },
  },
  {
    sector: 'operations',
    tag: { fr: 'Corporate', en: 'Corporate' },
    title: { fr: 'Reporting opérationnel automatisé', en: 'Automated operational reporting' },
    fullText: {
      fr: 'Automatiser le reporting opérationnel et libérer du temps pour les équipes métiers sur des sujets à fort impact.',
      en: 'Automate operational reporting and free up business teams to focus on high-impact topics.',
    },
    prompt: {
      fr: 'Le reporting opérationnel est trop manuel',
      en: 'Operational reporting is too manual',
    },
  },
  {
    sector: 'education',
    tag: { fr: 'Éducation / EdTech', en: 'Education / EdTech' },
    title: { fr: 'Parcours apprenant adaptatif', en: 'Adaptive learner path' },
    fullText: {
      fr: 'Identifier les blocages pédagogiques et proposer des parcours personnalisés pour augmenter l’engagement et la réussite.',
      en: 'Identify pedagogical blockers and recommend personalized paths to boost engagement and success.',
    },
    prompt: {
      fr: 'Les apprenants abandonnent parce que le parcours n’est pas adapté',
      en: 'Learners drop out because the path is not adapted',
    },
  },
];

const sectorIconMap: Record<string, 'target' | 'rocket' | 'chart' | 'check'> = {
  pharma: 'target',
  'supply-chain': 'rocket',
  industrial: 'chart',
  legal: 'check',
  finance: 'chart',
  operations: 'target',
  education: 'rocket',
};

type Stage = 'input' | 'analyzing' | 'result' | 'editor';

const REASONING_MESSAGES = {
  fr: [
    {
      author: 'strategist' as const,
      message: {
        fr: 'On reformule la friction en objectif métier mesurable.',
        en: 'We reframe the friction as a measurable business objective.',
      },
    },
    {
      author: 'domain-expert' as const,
      message: {
        fr: 'On identifie la North Star et les garde-fous associés.',
        en: 'We identify the North Star and its paired guardrails.',
      },
    },
    {
      author: 'product-lead' as const,
      message: {
        fr: 'On définit les métriques produit et les critères d’acceptation.',
        en: 'We define product metrics and acceptance criteria.',
      },
    },
    {
      author: 'data-lead' as const,
      message: {
        fr: 'On ajoute le plan d’instrumentation et les règles de décision.',
        en: 'We add the instrumentation plan and decision rules.',
      },
    },
  ],
  en: [
    {
      author: 'strategist' as const,
      message: {
        fr: 'On reformule la friction en objectif métier mesurable.',
        en: 'We reframe the friction as a measurable business objective.',
      },
    },
    {
      author: 'domain-expert' as const,
      message: {
        fr: 'On identifie la North Star et les garde-fous associés.',
        en: 'We identify the North Star and its paired guardrails.',
      },
    },
    {
      author: 'product-lead' as const,
      message: {
        fr: 'On définit les métriques produit et les critères d’acceptation.',
        en: 'We define product metrics and acceptance criteria.',
      },
    },
    {
      author: 'data-lead' as const,
      message: {
        fr: 'On ajoute le plan d’instrumentation et les règles de décision.',
        en: 'We add the instrumentation plan and decision rules.',
      },
    },
  ],
};

export default function NorthStarDashboardPage() {
  const { lang } = useParams<{ lang: string }>();
  const isEn = lang === 'en';
  const language: 'fr' | 'en' = isEn ? 'en' : 'fr';
  const l = (obj: { fr: string; en: string }) => (isEn ? obj.en : obj.fr);

  const pathname = usePathname();
  const searchParams = useSearchParams();
  const promptParam = searchParams?.get('prompt') || '';
  const sectorParam = searchParams?.get('sector') || '';

  const [sector, setSector] = useState(sectorParam);
  const [prompt, setPrompt] = useState(promptParam);
  const [stage, setStage] = useState<Stage>('input');
  const [plan, setPlan] = useState<MeasurementPlan | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [analysisComplete, setAnalysisComplete] = useState(false);
  const [chatComplete, setChatComplete] = useState(false);
  const [showLeadModal, setShowLeadModal] = useState(false);

  // Expose a helper for visual regression and E2E tests
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    (
      window as unknown as { loadMeasurementPlanDemo?: (demoPlan: MeasurementPlan) => void }
    ).loadMeasurementPlanDemo = (demoPlan: MeasurementPlan, showLeadModal = false) => {
      setPlan(demoPlan);
      setStage('result');
      setShowLeadModal(showLeadModal);
    };
  }, []);

  const t = useMemo(
    () => ({
      featureName: isEn ? 'North-Star Cockpit' : 'Cockpit North Star',
      title: isEn
        ? 'Turn your MVP idea into a measurement plan'
        : 'Transformez votre idée MVP en plan de mesure',
      lead: isEn
        ? 'Describe a friction. We define your North Star, the metrics to prove it, and how to instrument them.'
        : 'Décrivez une friction. Nous définissons votre North Star, les métriques pour le prouver, et comment les instrumenter.',
      analyzing: isEn ? 'Building your cockpit' : 'Construction du cockpit',
      painPointsTitle: isEn
        ? 'Pain points we help resolve'
        : 'Frictions que nous aidons à résoudre',
      painPointsSubtitle: isEn
        ? 'Pick an example to see how it becomes a measurable MVP.'
        : 'Choisissez un exemple pour voir comment il devient un MVP mesurable.',
      bookCta: isEn ? 'Book a 20-min strategy call' : 'Réserver un appel stratégique de 20 min',
      bookHref: `/${lang}/rendez-vous`,
      restart: isEn ? 'New scan' : 'Nouveau scan',
      backToCockpit: isEn ? 'Back to cockpit' : 'Retour au cockpit',
      trustItems: [
        { icon: Clock, label: isEn ? '20-min call' : 'Appel de 20 min' },
        { icon: Lock, label: isEn ? 'Privacy-first' : 'Confidentialité' },
        { icon: UserCheck, label: isEn ? 'Human expert review' : 'Revue humaine' },
        { icon: ExternalLink, label: isEn ? 'No lock-in' : 'Sans engagement' },
      ],
    }),
    [isEn, lang]
  );

  useEffect(() => {
    if (!promptParam || stage !== 'input') {
      return;
    }
    setSector(sectorParam);
    setPrompt(promptParam);
    void submit(promptParam, sectorParam);
  }, [promptParam, sectorParam, stage]);

  async function submit(inputPrompt: string, inputSector: string) {
    if (!inputPrompt.trim()) {
      return;
    }

    setStage('analyzing');
    setError(null);
    setPlan(null);
    setAnalysisComplete(false);
    setChatComplete(false);
    setShowLeadModal(false);

    trackMeasurementPlanEvent('mvp_plan_analysis_started', {
      path: pathname || '',
      lang: language,
    });

    try {
      const res = await fetch('/api/mvp-measurement-plan', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sector: inputSector,
          prompt: inputPrompt,
          lang: language,
        }),
      });

      if (!res.ok) {
        throw new Error('Failed');
      }

      const json = (await res.json()) as { plan: MeasurementPlan };
      setPlan(json.plan);
      trackMeasurementPlanEvent('mvp_plan_preview_generated', {
        path: pathname || '',
        lang: language,
      });
    } catch {
      // Stay on the analyzing stage until the reasoning chat finishes:
      // switching to the input form mid-animation reads as a failure/glitch.
      setError(t.lead);
    }
  }

  function handleManualSubmit() {
    void submit(prompt, sector);
  }

  function reset() {
    setSector('');
    setPrompt('');
    setPlan(null);
    setError(null);
    setStage('input');
    setAnalysisComplete(false);
    setChatComplete(false);
    setShowLeadModal(false);
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  function handleAnalysisComplete() {
    setAnalysisComplete(true);
    setChatComplete(true);
  }

  // Switch to the result only when BOTH the reasoning chat has finished AND
  // the plan has arrived. Otherwise the user would briefly see the input form
  // while the API response is still in flight.
  useEffect(() => {
    if (!chatComplete) {
      return;
    }
    if (plan) {
      setStage('result');
      setShowLeadModal(true);
    } else if (error) {
      setStage('input');
    }
  }, [chatComplete, plan, error]);

  const workShowcaseContexts = useMemo(
    () =>
      MVP_SUGGESTIONS.map((item) => ({
        title: l(item.title),
        description: l(item.fullText),
        gain: l(item.tag),
        icon: sectorIconMap[item.sector],
      })),
    [isEn]
  );

  const planMarkdown = useMemo(
    () => (plan ? buildConfluenceMarkdown(plan, language) : ''),
    [plan, language]
  );

  function renderHeader() {
    return (
      <header className={styles.topBar}>
        <div className={styles.brand}>
          <Plane size={18} />
          <span>{t.featureName}</span>
        </div>
        {stage !== 'input' && (
          <Link href={t.bookHref} className={styles.topCta}>
            <Calendar size={14} />
            {t.bookCta}
          </Link>
        )}
      </header>
    );
  }

  if (stage === 'analyzing') {
    return (
      <main className={styles.page}>
        {renderHeader()}
        <section className={styles.dashboard}>
          <GraphLoader label={t.analyzing} progress={analysisComplete ? (plan ? 100 : 90) : 35} />
          <ReasoningChat
            messages={REASONING_MESSAGES[language]}
            isEn={isEn}
            onComplete={handleAnalysisComplete}
          />
        </section>
      </main>
    );
  }

  if (stage === 'result' && plan) {
    return (
      <main className={styles.page}>
        {renderHeader()}
        <section className={styles.dashboard}>
          <CockpitResult
            plan={plan}
            isEn={isEn}
            lang={language}
            onTrack={(type) =>
              trackMeasurementPlanEvent(type, { path: pathname || '', lang: language })
            }
          />
          <div className={styles.dashboardSubFooter}>
            <div className={styles.footerActions}>
              <button type="button" className={styles.restart} onClick={() => setStage('editor')}>
                {isEn ? 'Open full editor' : 'Ouvrir l’éditeur complet'}
              </button>
              <button type="button" className={styles.restart} onClick={reset}>
                {t.restart}
              </button>
            </div>
          </div>
        </section>
        <LeadForm
          planTitle={plan.title}
          planMarkdown={planMarkdown}
          isEn={isEn}
          lang={language}
          isOpen={showLeadModal}
          onClose={() => setShowLeadModal(false)}
          onTrack={(type) =>
            trackMeasurementPlanEvent(type, { path: pathname || '', lang: language })
          }
        />
      </main>
    );
  }

  if (stage === 'editor' && plan) {
    return (
      <main className={styles.page}>
        {renderHeader()}
        <section className={styles.dashboard}>
          <FullPlanEditor
            initialPlan={plan}
            isEn={isEn}
            lang={language}
            onTrack={(type) =>
              trackMeasurementPlanEvent(type, { path: pathname || '', lang: language })
            }
          />
          <div className={styles.dashboardSubFooter}>
            <div className={styles.footerActions}>
              <button type="button" className={styles.restart} onClick={() => setStage('result')}>
                {t.backToCockpit}
              </button>
              <button type="button" className={styles.restart} onClick={reset}>
                {t.restart}
              </button>
            </div>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main className={styles.page}>
      {renderHeader()}
      <section className={styles.hero}>
        <div className={styles.heroInner}>
          <div className={styles.heroText}>
            <span className={styles.eyebrow}>
              <Radar size={14} />
              MVP-Lab
            </span>
            <h1 className={styles.heroTitle}>{t.title}</h1>
            <p className={styles.heroLead}>{t.lead}</p>
            <div className={styles.heroTrust}>
              {t.trustItems.map((item, idx) => (
                <div key={idx} className={styles.heroTrustBadge}>
                  <item.icon size={14} />
                  <span>{item.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className={styles.card}>
            <PromptForm
              sector={sector}
              sectors={SECTORS}
              prompt={prompt}
              isAnalyzing={false}
              isEn={isEn}
              onSectorChange={setSector}
              onPromptChange={setPrompt}
              onSubmit={handleManualSubmit}
            />
          </div>
        </div>

        {error && (
          <div className={styles.error}>
            <ErrorState isEn={isEn} onRetry={handleManualSubmit} />
          </div>
        )}
      </section>

      <SectorContexts
        eyebrow={isEn ? 'Use cases' : 'Cas d’usage'}
        title={t.painPointsTitle}
        description={t.painPointsSubtitle}
        contexts={workShowcaseContexts}
      />
    </main>
  );
}
